import { ACTION_LABEL } from "@/lib/i18nLabels";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { formatDetail } from "@/lib/api";
import { formatIDR } from "@/lib/constants";
import { toDateInput, toIso } from "./project-dialog/utils";
import PPNBreakdown from "@/components/PPNBreakdown";
import EditVOFields from "./project-dialog/EditVOFields";
import EditWOFields from "./project-dialog/EditWOFields";
import EditNewProjectFields from "./project-dialog/EditNewProjectFields";
import EditHistorySection from "./project-dialog/EditHistorySection";

export default function EditProjectDialog({ project, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [clientOptions, setClientOptions] = useState([]);
  const [picOptions, setPicOptions] = useState([]);
  const [financePicOptions, setFinancePicOptions] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/active-projects").then(({ data }) => {
      const uniq = Array.from(
        new Set(
          (data || [])
            .filter((p) => p.project_type === "new_project")
            .map((p) => (p.client || "").trim())
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b));
      setClientOptions(uniq);
    }).catch((e) => console.warn("Failed to load client suggestions", e));
    // 2026-06-19 (user request): opsi PIC = staff divisi Project SAJA.
    api.get("/users").then(({ data }) => {
      const active = (data || []).filter((u) => u.is_active !== false && !u.disabled_at);
      const staff = active
        .filter((u) => u.division === "Project")
        .map((u) => u.name)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
      setPicOptions(staff);
      // PIC Keuangan (2026-06-20): bebas — semua user aktif bisa ditunjuk.
      setFinancePicOptions(active.map((u) => u.name).filter(Boolean).sort((a, b) => a.localeCompare(b)));
    }).catch((e) => console.warn("Failed to load PIC options", e));
  }, [open]);

  const isVO = project?.project_type === "variation_order";
  const isWO = project?.project_type === "working_order";
  // 149-aj: pindah parent hanya diizinkan untuk WO/VO. Kalau project sudah completed,
  // ikuti aturan edit yg sama (BOD Lv1 super-admin only via backend). UI-level kita
  // sederhana: kalau bisa edit (dialog terbuka), maka boleh pindah parent.
  const canChangeParent = isVO || isWO;

  useEffect(() => {
    if (open && project) {
      setForm({
        project_name: project.project_name || "",
        client: project.client || "",
        city: project.city || "",
        province: project.province || "",
        start_date: toDateInput(project.start_date),
        end_date: toDateInput(project.end_date),
        contract_value: project.contract_value != null ? String(project.contract_value) : "",
        retention_pct: project.retention_pct != null ? Number(project.retention_pct) : 0,
        retention_period_days: project.retention_period_days != null ? Number(project.retention_period_days) : 0,
        retention_type: project.retention_type || "inside",
        vo_aggregation: project.vo_aggregation || "separate",
        is_ppn_exempt: !!project.is_ppn_exempt,
        variation_order_name: project.variation_order_name || "",
        vo_start_date: toDateInput(project.vo_start_date),
        vo_end_date: toDateInput(project.vo_end_date),
        wo_number: project.wo_number || "",
        wo_title: project.wo_title || "",
        work_description: project.work_description || "",
        wo_date: toDateInput(project.wo_date),
        po_number: project.po_number || "",
        po_date: toDateInput(project.po_date),
        po_value: project.po_value != null ? String(project.po_value) : "",
        sph_submit_date: toDateInput(project.sph_submit_date),
        sph_submissions: Array.isArray(project.sph_submissions) ? project.sph_submissions.map((r) => ({ ...r, date: (r.date || "").slice(0, 10) })) : [],
        site_survey_date: toDateInput(project.site_survey_date),
        project_done_date: toDateInput(project.project_done_date),
        invoice_sent_date: toDateInput(project.invoice_sent_date),
        // 149-aj: track current parent supaya bisa detect change & submit hanya bila beda
        parent_project_id: project.parent_project_id || null,
        // Catatan manual "Urgent Case WO" (display-only, override auto bucket).
        perlu_tindakan_manual: project.perlu_tindakan_manual || "",
        // 149-cw-r86 / 2026-06-20 (tri-state ketat): kategori WO — pertahankan
        // null (Belum Diklasifikasikan); JANGAN mengisi otomatis saat edit.
        is_urgent: project.is_urgent === true ? true : project.is_urgent === false ? false : null,
        // 2026-06-19: PIC project (pilihan staff divisi Project).
        pic_name: project.pic_name || "",
        pic_finance_name: project.pic_finance_name || "",
      });
      setShowHistory(false);
    }
  }, [open, project]);

  if (!project) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = isVO
        ? {
            variation_order_name: form.variation_order_name,
            vo_start_date: toIso(form.vo_start_date),
            vo_end_date: toIso(form.vo_end_date),
            contract_value: form.contract_value === "" ? null : parseFloat(form.contract_value),
            retention_pct: Number(form.retention_pct) || 0,
            retention_period_days: Number(form.retention_period_days) || 0,
            retention_type: form.retention_type || "inside",
            // 149-aj: pindah parent (hanya kirim kalau beda dari original)
            ...(form.parent_project_id && form.parent_project_id !== project.parent_project_id
              ? { parent_project_id: form.parent_project_id }
              : {}),
          }
        : isWO
        ? {
            wo_number: form.wo_number,
            wo_title: (form.wo_title || "").trim() || null,
            work_description: (form.work_description || "").trim() || null,
            wo_date: toIso(form.wo_date),
            po_number: form.po_number || null,
            po_date: toIso(form.po_date),
            // po_value auto-mirrors contract_value (PO Value input removed — Contract Value is single source of truth)
            po_value: form.contract_value === "" ? null : parseFloat(form.contract_value),
            sph_submit_date: toIso(form.sph_submit_date),
            // 149-cw-r14: Multi-SPH — kirim array kalau ada, backend derive latest date.
            sph_submissions: (form.sph_submissions || []).filter((r) => r.date),
            site_survey_date: toIso(form.site_survey_date),
            project_done_date: toIso(form.project_done_date),
            invoice_sent_date: toIso(form.invoice_sent_date),
            contract_value: form.contract_value === "" ? null : parseFloat(form.contract_value),
            retention_pct: Number(form.retention_pct) || 0,
            retention_period_days: Number(form.retention_period_days) || 0,
            retention_type: form.retention_type || "inside",
            // 149-aj: pindah parent (hanya kirim kalau beda dari original)
            ...(form.parent_project_id && form.parent_project_id !== project.parent_project_id
              ? { parent_project_id: form.parent_project_id }
              : {}),
            // Catatan manual "Urgent Case WO" (display-only). Trim → null saat kosong.
            perlu_tindakan_manual: (form.perlu_tindakan_manual || "").trim() || null,
            // 149-cw-r86 / 2026-06-20 (tri-state ketat): kirim is_urgent HANYA
            // bila sudah diklasifikasikan (true/false). null = belum
            // diklasifikasikan → field TIDAK ditulis (jangan auto-isi data kosong).
            ...(form.is_urgent === true || form.is_urgent === false ? { is_urgent: form.is_urgent } : {}),
          }
        : {
            project_name: form.project_name,
            client: form.client,
            city: form.city || null,
            province: form.province || null,
            start_date: toIso(form.start_date),
            end_date: toIso(form.end_date),
            contract_value: form.contract_value === "" ? null : parseFloat(form.contract_value),
            retention_pct: Number(form.retention_pct) || 0,
            retention_period_days: Number(form.retention_period_days) || 0,
            retention_type: form.retention_type || "inside",
            vo_aggregation: form.vo_aggregation || "separate",
            is_ppn_exempt: !!form.is_ppn_exempt,
            pic_name: (form.pic_name || "").trim() || null,
            pic_finance_name: (form.pic_finance_name || "").trim() || null,
          };
      const { data } = await api.patch(`/active-projects/${project.id}`, payload);
      if (Array.isArray(data.changes) && data.changes.length === 0) {
        toast.info("No changes to save");
      } else {
        toast.success("Project updated");
      }
      // Auto-cascade PPN ke sub-projects saat parent (New Project) ubah is_ppn_exempt.
      // User request 10-Feb-2026: "ketika status ppn dirubah di parent, maka turunan project
      // lainnya juga akan sama". Backend endpoint copy parent.is_ppn_exempt ke semua children.
      if (!isVO && !isWO && !!form.is_ppn_exempt !== !!project.is_ppn_exempt) {
        try {
          const { data: casc } = await api.post(`/active-projects/${project.id}/cascade-ppn-exempt`);
          if (casc?.updated > 0) {
            toast.success(`${casc.updated} sub-project auto-sync → ${casc.is_ppn_exempt ? "Non-PPN" : "kena PPN 11%"}`);
          }
        } catch (err) {
          // Non-blocking — save sudah sukses; hanya toast warning
          toast.warning("Parent tersimpan, tapi auto-cascade PPN ke sub-project gagal. Buka lagi Edit atau hubungi admin.");
        }
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const history = [...(project.edit_history || [])].reverse();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="dialog-edit-project">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Edit {isVO ? "Variation Order" : "Project"}
          </DialogTitle>
          <DialogDescription>
            {isVO
              ? "Parent project name & client are inherited and cannot be changed here."
              : "Update project header details. Changes are recorded in edit history."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex flex-col flex-1 min-h-0">
          <div className="space-y-4 overflow-y-auto flex-1 pr-1">
          {isVO ? (
            <EditVOFields project={project} form={form} setForm={setForm} canChangeParent={canChangeParent} />
          ) : isWO ? (
            <EditWOFields project={project} form={form} setForm={setForm} canChangeParent={canChangeParent} />
          ) : (
            <EditNewProjectFields form={form} setForm={setForm} clientOptions={clientOptions} />
          )}

          {/* 2026-06-19 (user request): kolom PIC — pilihan terbatas staff divisi
              Project. PIC lama yg bukan staff Project tetap ditampilkan sebagai
              opsi agar tidak hilang saat edit. */}
          {!isVO && !isWO && (
            <div className="space-y-1.5" data-testid="edit-proj-pic-section">
              <Label className="overline text-slate-600">PIC (Penanggung Jawab)</Label>
              <select
                data-testid="edit-proj-pic"
                value={form.pic_name || ""}
                onChange={(e) => setForm({ ...form, pic_name: e.target.value })}
                className="w-full border border-slate-300 px-2 py-2 text-xs bg-white rounded-none focus:outline-none focus:border-[#0B1D3A]"
              >
                <option value="">— Belum ditentukan —</option>
                {form.pic_name && !picOptions.includes(form.pic_name) && (
                  <option value={form.pic_name}>{form.pic_name} (saat ini)</option>
                )}
                {picOptions.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <p className="text-[10px] text-slate-500">Pilihan hanya menampilkan staff divisi <b>Project</b>.</p>
            </div>
          )}

          {/* PIC Keuangan (2026-06-20 user request): bebas — siapa saja yang
              ditunjuk oleh yang memiliki akses. */}
          {!isVO && !isWO && (
            <div className="space-y-1.5" data-testid="edit-proj-pic-finance-section">
              <Label className="overline text-slate-600">PIC Keuangan</Label>
              <select
                data-testid="edit-proj-pic-finance"
                value={form.pic_finance_name || ""}
                onChange={(e) => setForm({ ...form, pic_finance_name: e.target.value })}
                className="w-full border border-slate-300 px-2 py-2 text-xs bg-white rounded-none focus:outline-none focus:border-[#0B1D3A]"
              >
                <option value="">— Belum ditentukan —</option>
                {form.pic_finance_name && !financePicOptions.includes(form.pic_finance_name) && (
                  <option value={form.pic_finance_name}>{form.pic_finance_name} (saat ini)</option>
                )}
                {financePicOptions.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <p className="text-[10px] text-slate-500">Bebas — dapat diisi siapa saja yang ditunjuk.</p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="overline text-slate-600">Contract Value (IDR) <span className="normal-case text-slate-400 text-[9px]">(DPP)</span></Label>
            <Input
              data-testid="edit-proj-contract"
              type="number"
              min="0"
              step="1"
              value={form.contract_value}
              onChange={(e) => setForm({ ...form, contract_value: e.target.value })}
              className="rounded-none tabular-nums"
              placeholder="0"
            />
            {/* Toggle Non-PPN HANYA di parent project (New Project). VO/WO otomatis
                inherit dari parent via cascade endpoint. User request 10-Feb-2026. */}
            {!isVO && !isWO ? (
              <>
                <label className="flex items-center gap-1.5 text-[10px] cursor-pointer select-none" data-testid="edit-non-ppn-toggle">
                  <input type="checkbox" checked={!!form.is_ppn_exempt} onChange={(e) => setForm({ ...form, is_ppn_exempt: e.target.checked })} className="h-3 w-3 accent-[#FF5A1F]" />
                  <span className="uppercase tracking-wider font-semibold text-slate-600">Project Non-PPN (bebas PPN 11%) — akan meng-update semua VO/WO otomatis</span>
                </label>
                <PPNBreakdown value={form.contract_value} isExempt={!!form.is_ppn_exempt} testid="edit-ppn-breakdown" />
              </>
            ) : (
              <p className="text-[10px] text-slate-500 italic" data-testid="edit-ppn-inherit-info">
                Status PPN mengikuti parent project ({project.is_ppn_exempt ? "Non-PPN" : "kena PPN 11%"}).
              </p>
            )}
            <p className="text-[10px] text-slate-500 tabular-nums">
              {form.contract_value ? formatIDR(parseFloat(form.contract_value) || 0) : ""}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="overline text-slate-600">Retention</Label>
            <div className="inline-flex border border-slate-300 text-xs w-full" data-testid="edit-proj-retention">
              {[
                { v: 0, label: "None" },
                { v: 5, label: "5%" },
                { v: 10, label: "10%" },
              ].map((r) => (
                <button
                  key={r.v}
                  type="button"
                  data-testid={`edit-proj-retention-${r.v}`}
                  onClick={() => setForm({ ...form, retention_pct: r.v })}
                  className={`flex-1 px-3 py-2 uppercase tracking-wider font-semibold ${Number(form.retention_pct) === r.v ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-slate-500">Applied to future billing entries only. Past entries keep their original retention snapshot.</p>
          </div>

          {Number(form.retention_pct) > 0 && (
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Jenis Retensi</Label>
              <div className="inline-flex border border-slate-300 text-xs w-full" data-testid="edit-proj-retention-type">
                {[
                  { v: "inside", label: "Ditahan per penagihan" },
                  { v: "outside", label: "Ditahan pada akhir penagihan" },
                ].map((r) => (
                  <button key={r.v} type="button" data-testid={`edit-proj-retention-type-${r.v}`}
                    onClick={() => setForm({ ...form, retention_type: r.v })}
                    className={`flex-1 px-3 py-2 uppercase tracking-wider font-semibold ${(form.retention_type || "inside") === r.v ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                    {r.label}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-slate-500 leading-relaxed">
                <b className="text-[#0B1D3A]">Ditahan per penagihan</b>: retensi dipotong dari setiap invoice → Received Net = Invoice − PPh − Retensi.<br/>
                <b className="text-[#0B1D3A]">Ditahan pada akhir penagihan</b>: setiap invoice bebas potongan retensi (max billing = kontrak × (100−r)%). Retensi ditagih terpisah di invoice terakhir.
              </p>
            </div>
          )}

          {Number(form.retention_pct) > 0 && (
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Retention Period (Masa Retensi)</Label>
              <div className="relative">
                <Input
                  data-testid="edit-proj-retention-period-days"
                  type="number"
                  min="0"
                  max="3650"
                  step="1"
                  value={form.retention_period_days ?? 0}
                  onChange={(e) => setForm({ ...form, retention_period_days: e.target.value })}
                  className="rounded-none pr-14 tabular-nums"
                  placeholder="0"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs">Hari</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {[30, 90, 180, 365].map((d) => (
                  <button key={d} type="button" data-testid={`edit-proj-retention-preset-${d}`}
                    onClick={() => setForm({ ...form, retention_period_days: d })}
                    className={`text-[10px] px-2 py-0.5 border transition-colors ${Number(form.retention_period_days) === d ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "border-slate-300 text-slate-600 hover:border-[#FF5A1F] hover:text-[#FF5A1F]"}`}>
                    {d === 365 ? "1 Tahun" : `${d} Hari`}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-slate-500">
                Masukkan durasi masa retensi dalam <span className="font-semibold">hari</span> (0 = belum ditentukan). Sistem akan mengingatkan bila retensi belum diklaim melewati periode ini setelah proyek selesai.
                {Number(form.retention_period_days) > 0 && (
                  <span className="ml-1 text-amber-700 font-mono">≈ {(Number(form.retention_period_days) / 30).toFixed(1)} bulan</span>
                )}
              </p>
            </div>
          )}

          {/* VO Aggregation — hanya untuk New Project (parent). Toggle di sini karena
              tidak boleh diubah dari halaman VO/WO sendiri. Ditaruh dalam scrollable
              div supaya Cancel/Save Changes tetap pinned di bottom. */}
          {!isVO && !isWO && (
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Perhitungan VO</Label>
              <div className="inline-flex border border-slate-300 text-xs w-full" data-testid="edit-proj-vo-aggregation">
                {[
                  { v: "separate", label: "VO Dipisah" },
                  { v: "combined", label: "VO Digabung" },
                ].map((r) => (
                  <button key={r.v} type="button" data-testid={`edit-proj-vo-agg-${r.v}`}
                    onClick={() => setForm({ ...form, vo_aggregation: r.v })}
                    className={`flex-1 px-3 py-2 uppercase tracking-wider font-semibold ${(form.vo_aggregation || "separate") === r.v ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                    {r.label}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-slate-500 leading-relaxed">
                <b className="text-[#0B1D3A]">VO Dipisah</b>: setiap VO/WO tetap punya ledger sendiri.<br/>
                <b className="text-[#0B1D3A]">VO Digabung</b>: uang masuk & keluar semua VO/WO digabung di parent; limit tagihan = total kontrak parent + semua VO.
              </p>
            </div>
          )}

          <EditHistorySection history={history} show={showHistory} onToggle={() => setShowHistory((s) => !s)} />
          </div>

          {/* Footer buttons — pinned at the bottom of the dialog so Cancel/Save selalu terlihat
              tanpa perlu scroll (user request 08-Feb-2026). */}
          <DialogFooter className="border-t border-slate-200 pt-3 mt-3 bg-white flex-shrink-0">
            <Button type="button" variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>{ACTION_LABEL.cancel}</Button>
            <Button type="submit" data-testid="edit-proj-submit" disabled={busy} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]">
              {busy ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
